import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  StatusBadge,
  useConfirm,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { useEmployeeOptions } from '@/features/employees/api';
import { useImageUrl } from '@/features/products/api';
import { cn } from '@/lib/cn';
import { formatDate, formatDateTime } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { useOpsPhoto } from '../photos';
import { CampaignDialog } from './CampaignDialog';
import { ReviewQueue } from './ReviewQueue';
import {
  CAMPAIGN_FILTERS,
  campaignToValues,
  emptyCampaign,
  matchesFilter,
  missingCount,
  type BoardCampaign,
  type CampaignFilter,
  type CampaignValues,
} from './campaignValues';
import {
  displayStatusLabel,
  displayStatusTone,
  useAdminCampaigns,
  useDisplayMutations,
  useReviewQueue,
  useSubmissions,
  type Submission,
} from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * ויטרינה, בצד ההנהלה (PRD 5.9.3).
 *
 * השאלה שבגללה פותחים את המסך היא "מי עוד לא הגיש", ולכן הספירה
 * יושבת על הכרטיס ולא מאחורי לחיצה. הצילומים עצמם נפתחים לפי
 * דרישה, כי הם כבדים ורובם לא נחוצים ברגע הזה.
 */
export function DisplayAdminPage() {
  const confirm = useConfirm();
  const canManage = usePerm('ops.manage');
  const campaigns = useAdminCampaigns();
  const mutations = useDisplayMutations();
  const { data: options } = useEmployeeOptions();
  const branches = options?.branches ?? [];

  const queue = useReviewQueue();
  const [view, setView] = useState<'campaigns' | 'queue'>('campaigns');
  const [editing, setEditing] = useState<CampaignValues | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState<CampaignFilter>('all');
  const [branchId, setBranchId] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught: unknown) => setError(errorMessage(caught)));
  };

  const all = campaigns.data ?? [];
  const found = all.filter(
    (campaign) =>
      matchesFilter(campaign, filter) &&
      (branchId === '' || campaign.branches.some((branch) => branch.id === branchId)),
  );

  return (
    <AdminPage
      title="ויטרינה"
      subtitle="איך הסניפים צריכים להיראות, ומה הם העלו"
      backTo="/admin/products/planogram"
      actions={
        <Button variant="primary" onClick={() => setEditing(emptyCampaign)}>
          <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
          סידור חדש
        </Button>
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {/* שתי שאלות שונות, ולכן שתי תצוגות: "מה מצב הקמפיין"
          לעומת "מה מחכה לי עכשיו" */}
      <div className="flex flex-wrap gap-1">
        <ViewTab active={view === 'campaigns'} onClick={() => setView('campaigns')}>
          לפי סידור
        </ViewTab>
        <ViewTab active={view === 'queue'} onClick={() => setView('queue')}>
          תור אישורים
          {(queue.data?.length ?? 0) > 0 && (
            <span className="tabular rounded-pill bg-charcoal px-2 py-0.5 text-label text-on-charcoal">
              {queue.data?.length}
            </span>
          )}
        </ViewTab>
      </div>

      {view === 'queue' && <ReviewQueue />}

      {view === 'campaigns' && all.length > 0 && (
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-1">
            {CAMPAIGN_FILTERS.map((option) => (
              <button
                key={option.key}
                type="button"
                aria-pressed={filter === option.key}
                onClick={() => setFilter(option.key)}
                className={cn(
                  'min-h-11 cursor-pointer rounded-pill border px-3 text-body-sm transition-colors',
                  filter === option.key
                    ? 'border-ink bg-olive font-semibold text-ink'
                    : 'border-muted bg-transparent font-medium text-ink hover:bg-cream',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>

          {branches.length > 1 && (
            <label className="grid gap-1">
              <span className="text-label font-medium">סניף</span>
              <select
                value={branchId}
                onChange={(event) => setBranchId(event.target.value)}
                className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
              >
                <option value="">כל הסניפים</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      {view === 'campaigns' && campaigns.isPending && <Skeleton className="h-32" />}
      {view === 'campaigns' && campaigns.isError && (
        <ErrorState onRetry={() => void campaigns.refetch()} />
      )}

      {view === 'campaigns' && all.length === 0 && !campaigns.isPending && (
        <EmptyState
          title="אין סידורי ויטרינה"
          description="סידור הוא תמונת יעד והוראות, וכל סניף מעלה צילום של הביצוע."
        />
      )}

      {view === 'campaigns' && all.length > 0 && found.length === 0 && (
        <EmptyState title="אין סידור שמתאים" description="אפשר לשנות את הסינון." />
      )}

      <div className="grid gap-3">
        {view === 'campaigns' &&
          found.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              isOpen={open === campaign.id}
              onToggle={() => setOpen(open === campaign.id ? null : campaign.id)}
              onEdit={() => setEditing(campaignToValues(campaign))}
              onRemove={() =>
                confirm.ask({
                  title: 'מחיקת סידור ויטרינה',
                  body: (
                    <>
                      <p className="m-0">"{campaign.title}"</p>
                      <p className="m-0 text-body-sm text-muted">
                        הצילומים שסניפים כבר הגישו יימחקו איתו.
                      </p>
                    </>
                  ),
                  confirmLabel: 'מחיקת הסידור וההגשות',
                  onConfirm: () => run(() => mutations.removeCampaign.mutateAsync(campaign.id)),
                })
              }
            />
          ))}
      </div>

      {editing && (
        <CampaignDialog
          initial={editing}
          branches={branches}
          isSaving={mutations.saveCampaign.isPending}
          onSave={(values) =>
            mutations.saveCampaign.mutateAsync(values).then(() => setEditing(null))
          }
          onClose={() => setEditing(null)}
        />
      )}

      {confirm.dialog}
    </AdminPage>
  );
}

function ViewTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-pill border px-4 text-body transition-colors',
        active
          ? 'border-ink bg-olive font-semibold text-ink'
          : 'border-muted bg-transparent font-medium text-ink hover:bg-cream',
      )}
    >
      {children}
    </button>
  );
}

function CampaignCard({
  campaign,
  isOpen,
  onToggle,
  onEdit,
  onRemove,
}: {
  campaign: BoardCampaign;
  isOpen: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const reference = useImageUrl(campaign.reference_image_path);
  const missing = missingCount(campaign.counts);

  const today = new Date().toISOString().slice(0, 10);
  const isLate = Boolean(campaign.due_date && campaign.due_date < today && missing > 0);

  return (
    <Panel
      title={campaign.title}
      actions={
        <div className="flex items-center gap-1">
          <Button variant="ghost" onClick={onToggle} aria-expanded={isOpen}>
            {isOpen ? 'סגירת ההגשות' : 'ההגשות'}
          </Button>
          <Button variant="ghost" aria-label={`עריכת ${campaign.title}`} onClick={onEdit}>
            <Pencil size={20} strokeWidth={1.75} aria-hidden="true" />
          </Button>
          <Button variant="ghost" aria-label={`מחיקת ${campaign.title}`} onClick={onRemove}>
            <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
          </Button>
        </div>
      }
    >
      <div className="grid gap-3 sm:grid-cols-[auto_1fr] sm:items-start">
        {/* תמונת היעד בניהול, לא רק אצל העובד: אי אפשר לשפוט הגשה
            בלי לראות מולה את מה שביקשנו */}
        {reference.data && (
          <img
            src={reference.data}
            alt=""
            className="h-28 w-40 rounded-sm border border-line object-cover"
          />
        )}

        <div className="grid gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {campaign.due_date && (
              <span className={cn('text-label', isLate ? 'font-semibold text-bad' : 'text-muted')}>
                {isLate && '⚠ '}
                עד {formatDate(campaign.due_date)}
              </span>
            )}
            <span className="text-label text-muted">
              {campaign.is_targeted
                ? campaign.branches.map((branch) => branch.name).join(' · ')
                : 'כל הסניפים'}
            </span>
          </div>

          {/* המצב במבט אחד. צבע לבד אינו מספיק, ולכן כל מספר נושא
              את המילה שלו */}
          <div className="flex flex-wrap gap-2">
            {missing > 0 && <Tally tone="bad" count={missing} label="לא הגישו" />}
            {campaign.counts.submitted > 0 && (
              <Tally tone="warn" count={campaign.counts.submitted} label="ממתינות לאישור" />
            )}
            {campaign.counts.needs_fix > 0 && (
              <Tally tone="bad" count={campaign.counts.needs_fix} label="חזרו לתיקון" />
            )}
            {campaign.counts.approved > 0 && (
              <Tally tone="good" count={campaign.counts.approved} label="אושרו" />
            )}
          </div>

          {campaign.instructions && (
            <p className="m-0 whitespace-pre-line text-body-sm">{campaign.instructions}</p>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="mt-3 border-t border-line pt-3">
          <Submissions campaignId={campaign.id} />
        </div>
      )}
    </Panel>
  );
}

function Tally({
  tone,
  count,
  label,
}: {
  tone: 'good' | 'warn' | 'bad';
  count: number;
  label: string;
}) {
  return (
    <StatusBadge tone={tone}>
      <span className="tabular">{count}</span> {label}
    </StatusBadge>
  );
}

function Submissions({ campaignId }: { campaignId: string }) {
  const submissions = useSubmissions(campaignId);
  const { review } = useDisplayMutations();
  const [feedback, setFeedback] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  if (submissions.isPending) return <Skeleton className="h-24" />;

  const rows = submissions.data ?? [];
  if (rows.length === 0) {
    return <p className="m-0 text-body-sm text-muted">אף סניף עוד לא העלה צילום.</p>;
  }

  return (
    <div className="grid gap-3">
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}
      {rows.map((submission) => (
        <SubmissionCard
          key={submission.id}
          submission={submission}
          feedback={feedback[submission.id] ?? ''}
          onFeedback={(value) => setFeedback({ ...feedback, [submission.id]: value })}
          onReview={(approve) => {
            setError(null);
            review
              .mutateAsync({
                id: submission.id,
                approve,
                feedback: feedback[submission.id],
              })
              .catch((caught: unknown) => setError(errorMessage(caught)));
          }}
        />
      ))}
    </div>
  );
}

function SubmissionCard({
  submission,
  feedback,
  onFeedback,
  onReview,
}: {
  submission: Submission;
  feedback: string;
  onFeedback: (value: string) => void;
  onReview: (approve: boolean) => void;
}) {
  const photo = useOpsPhoto(submission.photo_path);

  return (
    <div className="grid gap-2 rounded-md border border-line p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body font-medium">{submission.branches?.name}</span>
        <span className="text-label text-muted">
          {submission.employees &&
            `${submission.employees.first_name} ${submission.employees.last_name}`}
          {' · '}
          {formatDateTime(submission.created_at)}
        </span>
        <span className="grow" />
        <StatusBadge tone={displayStatusTone[submission.status]}>
          {displayStatusLabel[submission.status]}
        </StatusBadge>
      </div>

      {photo.data && (
        <img src={photo.data} alt="" className="max-h-72 w-full rounded-sm object-contain" />
      )}

      {submission.feedback && (
        <p className="m-0 text-body-sm text-muted">הערה: {submission.feedback}</p>
      )}

      {submission.status === 'submitted' && (
        <div className="grid gap-2">
          <input
            value={feedback}
            onChange={(event) => onFeedback(event.target.value)}
            placeholder="הערה לסניף, לא חובה"
            aria-label="הערה לסניף"
            className={FIELD}
          />
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" onClick={() => onReview(true)}>
              <Check size={18} strokeWidth={1.75} aria-hidden="true" />
              אישור
            </Button>
            <Button variant="secondary" onClick={() => onReview(false)}>
              <X size={18} strokeWidth={1.75} aria-hidden="true" />
              החזרה לתיקון
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
